import React, { ComponentType, useCallback, useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { EventMappingRpcResponseItem, GetEventMappingOptions, GetEventMappingRpcResponse } from '@om-fe/api';
import isEmpty from 'lodash/isEmpty';
import { MatchScoreboard } from '@om-fe/widgets/dist/components/match-scoreboard/match-scoreboard.tsx';
import { BetradarDetailedScoreboard, BetradarLayout, BetradarMomentum, BetradarScoreboard, BetradarTabsPosition } from '@om-fe/widgets/dist/components/betradar/betradar-widget.constans.ts'
import { BetradarWidget } from '@om-fe/widgets/dist/components/betradar/betradar-widget.tsx';
import { MatchScoreLayout, MatchScoreScopes } from '@om-fe/widgets/dist/widgets/match-score/match-score.constants';
import { Spinner } from '@om-fe/widgets/dist/components/spinner/spinner';
import { useRpcState } from "@om-fe/widgets/dist/common/rpc/rpc-state.hooks";
import { WidgetType } from '@om-fe/widgets/dist/components/external-sport-widgets/widget.constants';
import { BrowserOnly } from '@om-fe/widgets/dist/components/ssr/browser-only';
import { BemMethod, createBem, filterStreamsByLanguage } from '@om-fe/utils';
import { EventTimeModel, MatchDetailsViewModel, DisciplineLiveStatus } from '@om-fe/models';
import { MatchDetailsHeaderContent } from './match-details-header-content';
import { BreakpointsState } from '@om-fe/core/core/store/breakpoints';
import { FALLBACK_LANGUAGE, I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import getConfig, { statScoreV2Item } from '@om-fe/core/utils/config';
import { matchDetailsHeaderBem } from '@om-fe/core/components/match-details/utils/match-details-header.utils';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { MatchDetailsStateExt, SLICE_MATCH_DETAILS } from '@om-fe/core/components/match-details/match-details.store';
import getSlice from '@om-fe/core/utils/get-slice';
import { dynamicDomain } from '@om-fe/core/utils/dynamic-domain';
import { StatScoreWidgetV2 } from '@om-fe/widgets/components/stat-score/statscore-widget';

export const matchDetailsHeaderLiveEventPageBem: BemMethod = createBem('MatchDetailsHeaderLiveEventPage');

const widgetScoreList: string[] = [
    WidgetType.OMScoreWidget,
    WidgetType.BrWidget,
    WidgetType.BrWidgetLMT,
    WidgetType.StatScoreV2,
];

interface MatchDetailsHeaderLiveEventPagePublicProps {
    readonly matchId: string;
    readonly lang: string;
    readonly disciplineId: string;
    readonly breakpoints: BreakpointsState | null;
}

interface MatchDetailsHeaderLiveEventPagePropsStoreProps {
    readonly hostname?: string;
    readonly event?: MatchDetailsViewModel | null;
    readonly eventTime?: EventTimeModel | null;
    readonly breakpoints: BreakpointsState;
    readonly isAuthenForTopic?: boolean;
}

type MatchDetailsHeaderLiveEventPageProps = MatchDetailsHeaderLiveEventPagePublicProps
    & MatchDetailsHeaderLiveEventPagePropsStoreProps
    & I18nContextInterface;

function MatchDetailsHeaderLiveEventPageComponent(props: MatchDetailsHeaderLiveEventPageProps) {
    const {
        matchId,
        disciplineId,
        lang,
        breakpoints,
        selectedLanguage,
        event,
        eventTime,
        isAuthenForTopic,
    } = props;

    const {
        betradar,
        i18nConfig: { defaultLanguage },
        statScoreWidgetV2Config,
    } = getConfig();

    const [scoreWidget, setScoreWidget] = useState<EventMappingRpcResponseItem | null>(null);
    const [streamingWidget, setStreamingWidget] = useState<EventMappingRpcResponseItem | null>(null);
    const [hasLicenseError, setHasLicenseError] = useState<boolean>(false);
    const statScoreConfig: statScoreV2Item | undefined = statScoreWidgetV2Config?.group;
    const clientId: string | undefined = betradar?.clientId;
    const [isEventMappingReady, setIsEventMappingReady] = useState<boolean>(false);
    const breakpointSmall: boolean = breakpoints.small;
    const hasBetradarWidget = !!(scoreWidget && (scoreWidget.key === WidgetType.BrWidget || scoreWidget.key === WidgetType.BrWidgetLMT) && clientId);
    const [waitScoreFilter, setWaitScoreFilter] = useState<boolean>(true);
    const logoPath: string | undefined = betradar && betradar.logo
        ? dynamicDomain({ urlTemplate: betradar.logo })
        : undefined;
    const pitchLogoPath: string | undefined = betradar && betradar.pitchLogo
        ? dynamicDomain({ urlTemplate: betradar.pitchLogo })
        : undefined;
    const goalBannerImagePath: string | undefined = betradar && betradar.goalBannerImage
        ? dynamicDomain({ urlTemplate: betradar.goalBannerImage })
        : undefined;

    useEffect(() => {
        return () => {
            setWaitScoreFilter(true);
            setScoreWidget(null);
        };
    }, [matchId]);

    const handleOnBetradarTrack = useCallback ((param1: string, param2: { readonly error: string }) => {
        if (param1 && param2.error) {
            setHasLicenseError(true);
        }
    }, []);

    const options: GetEventMappingOptions = useMemo(() => {
        return {
            matchId,
            lang,
        }
    }, [lang, matchId]);

    const [{data: eventMappingData}] = useRpcState<GetEventMappingRpcResponse>('eventMapping', options);

    useEffect(() => {
        setWaitScoreFilter(true);

        if (!eventMappingData) {
            setIsEventMappingReady(true);

            return;
        }

        let scoreWidgetValue: EventMappingRpcResponseItem | null = null;
        let streamingWidgetValue: EventMappingRpcResponseItem | null = null;
        
        if (statScoreConfig && (eventMappingData.STATS && !isEmpty(eventMappingData.STATS))) {
            eventMappingData.STATS.forEach((widget: EventMappingRpcResponseItem) => {
                const { key, value } = widget;
                if (!scoreWidgetValue && widgetScoreList.includes(key)) {
                    scoreWidgetValue = {
                        key,
                        value,
                    }
                }
            });
        }

        if (!statScoreConfig && eventMappingData.SCORE && !isEmpty(eventMappingData.SCORE)) {
            eventMappingData.SCORE.forEach((widget: EventMappingRpcResponseItem) => {
                const { key, value } = widget;
                if (!scoreWidgetValue && widgetScoreList.includes(key)) {
                    scoreWidgetValue = {
                        key,
                        value,
                    }
                }
            });
        }

        if (eventMappingData.STREAMING && eventMappingData.STREAMING[0]) {
            streamingWidgetValue = (
                filterStreamsByLanguage(
                    eventMappingData.STREAMING,
                    selectedLanguage,
                    defaultLanguage,
                    FALLBACK_LANGUAGE,
                )
            );
        }

        setStreamingWidget(streamingWidgetValue);
        setScoreWidget(scoreWidgetValue);
        setIsEventMappingReady(true);
        setHasLicenseError(false);
        setWaitScoreFilter(false);
    }, [defaultLanguage, eventMappingData, FALLBACK_LANGUAGE, lang, matchId, selectedLanguage])

    if (!isEventMappingReady) {
        return <Spinner/>
    }

    if ((!statScoreConfig || (statScoreConfig && streamingWidget && (!scoreWidget?.value || scoreWidget?.value !== WidgetType.StatScoreV2))) && breakpointSmall) {
        return (
            <section className={matchDetailsHeaderLiveEventPageBem()}>
                <MatchDetailsHeaderContent
                    scoreWidgetId={scoreWidget}
                    hasBetradarWidget={hasBetradarWidget}
                    streamingWidget={streamingWidget}
                    disciplineId={disciplineId}
                    clientId={clientId}
                    matchId={matchId}
                    event={event}
                    eventTime={eventTime}
                    liveStatus={DisciplineLiveStatus.LIVE}
                    isAuthenForTopic={isAuthenForTopic}
                />
            </section>
        );
    }

    return (
        <section className={matchDetailsHeaderBem('', { [`Sport-${disciplineId}`]: !!disciplineId })}>
            {hasBetradarWidget && !hasLicenseError && scoreWidget && clientId
                ?
                    betradar?.enabledRightSideBarLMT
                        ?
                            <MatchScoreboard
                                scopeName={MatchScoreScopes.Large}
                                matchId={matchId}
                                scoreLayout={MatchScoreLayout.V1}
                                disciplineId={disciplineId}
                                event={event}
                                eventTime={eventTime}
                                isAuthenForTopic={isAuthenForTopic}
                            />
                        :
                            <BetradarWidget
                                matchId={scoreWidget.value}
                                layout={BetradarLayout.Single}
                                detailedScoreboard={BetradarDetailedScoreboard.ScoreDetails}
                                momentum={BetradarMomentum.Timeline}
                                scoreboard={BetradarScoreboard.Compact}
                                tabsPosition={BetradarTabsPosition.Disable}
                                disableComponents={true}
                                disablePitch={true}
                                betradarId={clientId}
                                refId="LiveEventPageBetradarWidget"
                                logo={logoPath}
                                goalBannerCustomBgColor="#FFFFFF"
                                goalBannerImage={goalBannerImagePath}
                                pitchLogo={pitchLogoPath}
                                onBetradarTrack={handleOnBetradarTrack}
                                silent={true}
                            />
                :
                    statScoreConfig
                        ? scoreWidget?.value
                            ?
                                <BrowserOnly>
                                    <div className={matchDetailsHeaderBem('TopSection')}>
                                        <div className={matchDetailsHeaderBem('StatScoreStatistics')}>
                                            <StatScoreWidgetV2
                                                statscoreWidgetId={scoreWidget.value}
                                                configurationId={statScoreConfig.live}
                                                lang={selectedLanguage}
                                                statScoreWidgetV2Group={true}
                                            />
                                        </div>
                                    </div>
                                </BrowserOnly>
                            : !waitScoreFilter
                                ?
                                    <MatchScoreboard
                                        scopeName={MatchScoreScopes.Large}
                                        matchId={matchId}
                                        scoreLayout={MatchScoreLayout.V1}
                                        disciplineId={disciplineId}
                                        event={event}
                                        eventTime={eventTime}
                                        isAuthenForTopic={isAuthenForTopic}
                                    />
                                : null
                        : event && eventTime && !waitScoreFilter
                            ?
                                <MatchScoreboard
                                    scopeName={MatchScoreScopes.Large}
                                    matchId={matchId}
                                    scoreLayout={MatchScoreLayout.V1}
                                    disciplineId={disciplineId}
                                    event={event}
                                    eventTime={eventTime}
                                    isAuthenForTopic={isAuthenForTopic}
                                />
                            : null
            }
        </section>
    );
}

const MatchDetailsHeaderLiveEventPage: ComponentType<MatchDetailsHeaderLiveEventPagePublicProps> = compose(
    withTranslation<MatchDetailsHeaderLiveEventPagePublicProps>(),
    connect<MatchDetailsHeaderLiveEventPagePropsStoreProps, undefined, MatchDetailsHeaderLiveEventPagePublicProps, AppState>(
        (state: AppState): MatchDetailsHeaderLiveEventPagePropsStoreProps => {
            const matchSlice: MatchDetailsStateExt | undefined = getSlice(state, SLICE_MATCH_DETAILS);
            const { breakpoints, config: { hostname } } = state;
            const { enabledTopicDelay } = getConfig();
            const isAuthenForTopic: boolean = !!(enabledTopicDelay && state.session?.info?.isAuthenticated);

            if (!matchSlice) {
                return {
                    hostname,
                    breakpoints,
                }
            }

            const event: MatchDetailsViewModel | null = matchSlice?.event;
            const eventTime: EventTimeModel | null = matchSlice?.eventTime;

            return {
                hostname,
                event,
                eventTime,
                breakpoints,
                isAuthenForTopic,
            }
        }),
)(MatchDetailsHeaderLiveEventPageComponent);
export default MatchDetailsHeaderLiveEventPage;
